import { useMemo } from 'preact/hooks';
import { Fragment } from 'preact/jsx-runtime';
import Shipment from 'interfaces/PmParcel';
import { usePTPConfigContext } from 'GlobalConfig.context';
import {
  DEMO_TARGET,
  DEMO_TARGET_ATTRIBUTE,
} from 'constants/demoTarget.constants';
import { useTranslation } from 'react-i18next';
import { useDetectDevice } from 'hooks';

import {
  getShipmentDisplayFactory,
  ShipmentDisplay,
} from 'utils/shipmentDisplay.utils';

import { DisplayItem, Select } from './components';

import {
  Container,
  LeftItem,
  Title,
  RightItem,
  TitleContainer,
  ShipmentItems,
} from './Header.styles';

interface HeaderProps {
  shipments: Shipment[];
  selectedShipment: Shipment;
  onSelectShipment: (selectedShipment: Shipment) => void;
}

const Header: React.FC<HeaderProps> = ({
  shipments,
  selectedShipment,
  onSelectShipment,
}) => {
  const configs = usePTPConfigContext();
  const { isMobile } = useDetectDevice();
  const { t } = useTranslation();

  const handleOnSelectShipment = (value: string) => {
    const shipment = shipments.find(
      (shipment) => shipment.poId.toString() === value,
    );
    if (shipment) {
      onSelectShipment(shipment);
    }
  };

  const header = useMemo(() => {
    const totalShipment = shipments.length;
    const shipmentDisplayFactory = getShipmentDisplayFactory(
      configs,
      selectedShipment,
      t,
    );
    const title = shipmentDisplayFactory(ShipmentDisplay.orderSummary, {
      numbers: totalShipment,
    });

    if (totalShipment === 1 || totalShipment === 0) {
      return null;
    }

    if (isMobile) {
      const filteredShipments = shipments.filter(
        (item) => item.poId !== selectedShipment.poId,
      );

      const SelectedShipment = (
        <DisplayItem
          baseShipment={selectedShipment}
          numberOfShipment={totalShipment}
          isActiveShipment
          isDropdownItem
        />
      );

      return (
        <Fragment>
          <LeftItem data-testid="parcel-title">
            <TitleContainer>
              <Title>{title}</Title>
            </TitleContainer>
          </LeftItem>
          <RightItem
            data-testid="parcel-select"
            {...{ [DEMO_TARGET_ATTRIBUTE]: DEMO_TARGET.shipmentToggle }}
          >
            <Select
              options={filteredShipments}
              customDropdownTitle={SelectedShipment}
              onSelect={handleOnSelectShipment}
            />
          </RightItem>
        </Fragment>
      );
    }

    if (totalShipment === 2) {
      return (
        <Fragment>
          <LeftItem data-testid="parcel-title">
            <TitleContainer>
              <Title>{title}</Title>
            </TitleContainer>
          </LeftItem>
          <RightItem
            data-testid="parcel-select"
            {...{ [DEMO_TARGET_ATTRIBUTE]: DEMO_TARGET.shipmentToggle }}
          >
            {shipments.map((shipment, index) => {
              return (
                <DisplayItem
                  key={shipment.poId}
                  baseShipment={shipment}
                  numberOfShipment={totalShipment}
                  isActiveShipment={selectedShipment.poId === shipment.poId}
                  index={index}
                  onSelectShipment={onSelectShipment}
                />
              );
            })}
          </RightItem>
        </Fragment>
      );
    }

    if (totalShipment === 3) {
      return (
        <ShipmentItems
          data-testid="parcel-select"
          {...{ [DEMO_TARGET_ATTRIBUTE]: DEMO_TARGET.shipmentToggle }}
        >
          {shipments.map((shipment, index) => {
            return (
              <DisplayItem
                key={shipment.poId}
                baseShipment={shipment}
                numberOfShipment={totalShipment}
                isActiveShipment={selectedShipment.poId === shipment.poId}
                index={index}
                onSelectShipment={onSelectShipment}
              />
            );
          })}
        </ShipmentItems>
      );
    }

    if (totalShipment > 3) {
      const filteredShipments = shipments.filter(
        (item) => item.poId !== selectedShipment.poId,
      );

      const dropdownLabel = shipmentDisplayFactory(
        ShipmentDisplay.dropdownLabel,
        {
          number: filteredShipments.length,
        },
      );

      return (
        <Fragment>
          <LeftItem data-testid="parcel-title">
            <TitleContainer>
              <Title>{title}</Title>
            </TitleContainer>
          </LeftItem>
          <RightItem
            data-testid="parcel-select"
            {...{ [DEMO_TARGET_ATTRIBUTE]: DEMO_TARGET.shipmentToggle }}
          >
            <DisplayItem
              baseShipment={selectedShipment}
              numberOfShipment={totalShipment}
              isActiveShipment
            />
            <Select
              dropdownTitle={dropdownLabel}
              options={filteredShipments}
              onSelect={handleOnSelectShipment}
            />
          </RightItem>
        </Fragment>
      );
    }

    return null;
  }, [configs, selectedShipment, shipments, handleOnSelectShipment, t]);

  return <Container>{header}</Container>;
};

export default Header;
